import React, { useState, useEffect } from "react";
import { useAppSelector, useHandlePageNavigate } from "../../../hooks/hooks";
import { selectAdminStatus } from "../../../features/admin/adminSlice";
import {
  selectDownsellDetails,
  selectPauseDetails,
} from "../../../features/subscriptions/subscriptionSlice";

import {
  Padding,
  Layout,
  Button,
  ReportTitle,
  Text,
  Sheet,
  AnchorLink,
  Modal,
} from "@kidslivesafe/sc-react";
import UnpauseModal from "../../Dashboard/UnpauseModal";
import { useCancelSubscription } from "../../../hooks/cancelHook";

const AccountPaused: React.FC = () => {
  const pauseDetails = useAppSelector(selectPauseDetails);
  const adminStatus = useAppSelector(selectAdminStatus);
  const downsellDetails = useAppSelector(selectDownsellDetails);
  const cancelSubscription = useCancelSubscription();

  const [showContinuePauseModal, setShowContinuePauseModal] =
    useState<boolean>(false);
  const [toggleRetryModal, setToggleRetryModal] = useState<string | undefined>(
    undefined,
  );

  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useHandlePageNavigate();

  useEffect(() => {
    if (adminStatus === "loading") {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [adminStatus]);

  return (
    <>
      <ReportTitle reportTitle="Your Membership is Paused" />
      <Sheet>
        <Padding top="lg" bottom="lg" right="md" left="md">
          <Layout type="flex" flexProps={{ direction: "column", gap: "md" }}>
            <Text>
              Your membership is currently paused until{" "}
              {pauseDetails?.pauseBillingDate}
            </Text>
            <Text>
              Click "Resume" to restart your subscription -- your regular
              monthly rate will be charged today.
            </Text>
            <Text>
              Clicking "Continue Pause" will resume the pause for the remainder
              of your 30 days.
            </Text>
            <Text>
              You can cancel at any time by clicking "Cancel Subscription" and
              you will not be billed again.
            </Text>
            <Layout
              type="flex"
              flexProps={{
                justifyContent: "center",
                direction: "row",
                gap: "md",
              }}
              breakpointStyles={{
                mobile: {
                  type: "flex",
                  flexProps: {
                    justifyContent: "center",
                    gap: "xs",
                    direction: "column",
                  },
                },
              }}
            >
              <Button
                buttonText="Resume Subscription"
                handleClick={() => {
                  setToggleRetryModal(`${toggleRetryModal}a`);
                }}
                size="md"
                loading={loading}
                clicked={loading}
              />
              <Button
                buttonText="Continue Pause"
                handleClick={() => {
                  setShowContinuePauseModal(true);
                }}
                size="md"
                loading={loading}
                clicked={loading}
              />
            </Layout>
            <AnchorLink
              color="primary"
              role="button"
              size="base"
              hoverUnderline
              inline
              handleClick={() => {
                if (downsellDetails?.isDownsold) {
                  cancelSubscription();
                } else {
                  navigate("/cancel-options");
                }
              }}
            >
              Cancel Subscription
            </AnchorLink>
          </Layout>
        </Padding>
      </Sheet>
      <UnpauseModal toggleModal={toggleRetryModal} />
      <Modal
        open={showContinuePauseModal}
        setOpen={() => {
          setShowContinuePauseModal(false);
        }}
        size="fit-content"
        scrollable={false}
        xClose
      >
        <Padding space="sm" bottom="xl">
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "xl",
              alignItems: "center",
              justifyContent: "center",
            }}
            fillVertical
          >
            <Text textAlign="center" tag="h2" fontWeight="semibold">
              Success!{" "}
            </Text>
            <Layout
              type="flex"
              flexProps={{
                direction: "column",
                gap: "md",
                alignItems: "center",
              }}
              fillVertical
            >
              <Text>
                Your subscription will remain paused until{" "}
                {pauseDetails?.pauseBillingDate}.
              </Text>
              <Button
                buttonText="Accept"
                handleClick={() => {
                  setShowContinuePauseModal(false);
                }}
                size="md"
              />
            </Layout>
          </Layout>
        </Padding>
      </Modal>
    </>
  );
};

export default AccountPaused;
